import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import PersonalInfoSection from './components/PersonalInfoSection';
import JobSeekerSettings from './components/JobSeekerSettings';
import EmployerSettings from './components/EmployerSettings';
import PrivacySection from './components/PrivacySection';
import NotificationSection from './components/NotificationSection';
import SecuritySection from './components/SecuritySection';
import AccountSection from './components/AccountSection';

const UserProfileSettings = () => {
  const userRole = 'jobseeker';
  const [activeTab, setActiveTab] = useState('personal');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const jobSeekerTabs = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'job-preferences', label: 'Job Preferences', icon: 'Briefcase' },
    { id: 'privacy', label: 'Privacy & Data', icon: 'Shield' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'account', label: 'Account', icon: 'Settings' }
  ];

  const employerTabs = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'company', label: 'Company & Billing', icon: 'Building2' },
    { id: 'privacy', label: 'Privacy & Data', icon: 'Shield' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'account', label: 'Account', icon: 'Settings' }
  ];

  const tabs = userRole === 'employer' ? employerTabs : jobSeekerTabs;

  const renderContent = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalInfoSection userRole={userRole} />;
      case 'job-preferences':
        return <JobSeekerSettings />;
      case 'company':
        return <EmployerSettings />;
      case 'privacy':
        return <PrivacySection />;
      case 'notifications':
        return <NotificationSection />;
      case 'security':
        return <SecuritySection />;
      case 'account':
        return <AccountSection />;
      default:
        return <PersonalInfoSection userRole={userRole} />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Settings - JobPortal India</title>
        <meta name="description" content="Manage your account settings, preferences, and privacy options on JobPortal India" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <div className="pt-16">
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Profile Settings</h1>
                  <p className="text-sm text-muted-foreground mt-1">Manage your account preferences and settings</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row gap-6">
              <button
                onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                className="lg:hidden flex items-center justify-between p-4 bg-card border border-border rounded-lg"
              >
                <span className="text-sm font-medium text-foreground">
                  {tabs?.find(tab => tab?.id === activeTab)?.label}
                </span>
                <Icon name={isMobileSidebarOpen ? 'ChevronUp' : 'ChevronDown'} size={20} />
              </button>

              <aside className={`lg:block ${isMobileSidebarOpen ? 'block' : 'hidden'} w-full lg:w-64 flex-shrink-0`}>
                <div className="bg-card border border-border rounded-lg p-2 sticky top-20">
                  <nav className="space-y-1">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => {
                          setActiveTab(tab?.id);
                          setIsMobileSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-smooth ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={18} />
                        <span>{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              <main className="flex-1 min-w-0">
                {renderContent()}
              </main>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileSettings;